import { getApiClient, HttpError, NO_BACKOFF, useSDK } from '@littlebird/sdk';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useTabs } from '@/features/tabs/tabs';
import { logError } from '@/lib/errorLogging';

interface RevealChatResponse {
  chat_id: string;
  sync_id: number | null;
}

const revealChat = (chatId: string) =>
  getApiClient().fetch<RevealChatResponse>(`chat/${chatId}/reveal`, {
    method: 'POST',
    backoff: NO_BACKOFF,
  });

const openFailureMessage = (error: unknown): string => {
  if (error instanceof HttpError) {
    if (error.status === 404) return 'That chat no longer exists.';
    if (error.status === 409) return error.message;
  }
  return "Couldn't open that chat. Please try again.";
};

/** Reveals a chat the user reached from outside their chat list, then opens it in a tab. */
export const useOpenChat = () => {
  const sdk = useSDK();
  const { openTab } = useTabs();
  const [isOpening, setIsOpening] = useState(false);

  const openChat = useCallback(
    async (chatId: string) => {
      setIsOpening(true);
      try {
        const { sync_id: syncId } = await revealChat(chatId);
        if (syncId !== null) await sdk.syncNow();
        openTab({ path: `/chats/${chatId}` });
      } catch (error) {
        logError(error, 'Failed to open chat', {
          context: 'useOpenChat',
          metadata: { chatId },
        });
        toast.error(openFailureMessage(error));
      } finally {
        setIsOpening(false);
      }
    },
    [openTab, sdk]
  );

  return { openChat, isOpening };
};
